import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Signpost } from 'lucide-react';
import { CODE_OBTAINED_KEY, fetchCodeObtained, recordCodeObtained } from '../api/candidate';
import dayjs, { fmtDayFull } from '../lib/dates';
import { Button, Card, TextField } from './charte';
import { SchoolMessagesLink } from './SchoolMessagesLink';

// « Avez-vous déjà le code ? » (J46, 28/10/2026), sur l'accueil tant que la fiche n'a pas de date du code. « Oui » :
// la date, enregistrée sur la fiche. « Non » : « Demandez à votre auto-école comment vous entraîner au code de la
// route. », retenu dans le navigateur (la question ne revient pas sur cet appareil).

const noKey = (candidateId: string) => `pmd-candidat-code-non:${candidateId}`;

function readNo(candidateId: string): boolean {
  try {
    return localStorage.getItem(noKey(candidateId)) === '1';
  } catch {
    return false;
  }
}

export function CodeQuestion({ candidateId, onOpenMessages }: { candidateId: string; onOpenMessages: () => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: CODE_OBTAINED_KEY, queryFn: fetchCodeObtained, staleTime: 5 * 60 * 1000 });
  const [answer, setAnswer] = useState<'ask' | 'yes' | 'no'>('ask');
  const [answeredNo, setAnsweredNo] = useState(() => readNo(candidateId));
  const [day, setDay] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const today = dayjs().format('YYYY-MM-DD');
  const save = useMutation({
    mutationFn: () => recordCodeObtained(day),
    onSuccess: (view) => {
      setSaved(view.codeObtainedAt);
      queryClient.setQueryData(CODE_OBTAINED_KEY, view);
    },
  });

  if (saved)
    return (
      <Card role="status" className="flex items-center gap-3 !py-4 text-sm text-body">
        <Icon />
        C’est noté : code de la route obtenu le {fmtDayFull(saved, today)}. Votre auto-école le retrouve sur votre dossier.
      </Card>
    );
  if (!query.data?.asked || answeredNo) return null;

  const closeNo = () => {
    setAnsweredNo(true);
    try {
      localStorage.setItem(noKey(candidateId), '1');
    } catch {
      // stockage indisponible : la question reviendra au prochain chargement.
    }
  };

  return (
    <Card aria-labelledby="code-question">
      <div className="flex items-start gap-3">
        <Icon />
        <div className="min-w-0 flex-1">
          <h2 id="code-question" className="text-title-sm text-heading">
            Avez-vous déjà le code ?
          </h2>
          {answer === 'ask' && (
            <>
              <p className="mt-1 text-sm text-secondary">L’examen du code de la route (ETG), déjà réussi.</p>
              <div className="mt-3 flex gap-2">
                <Button variant="soft" onClick={() => setAnswer('yes')}>
                  Oui
                </Button>
                <Button onClick={() => setAnswer('no')}>Non</Button>
              </div>
            </>
          )}
          {answer === 'yes' && (
            <form
              className="mt-3 grid gap-3 sm:max-w-xs"
              onSubmit={(e) => {
                e.preventDefault();
                if (day) save.mutate();
              }}
            >
              <TextField
                label="Date d’obtention du code"
                type="date"
                max={today}
                value={day}
                onChange={(e) => setDay(e.target.value)}
                error={save.isError ? save.error.message || 'Enregistrement impossible. Réessayez.' : undefined}
                required
              />
              <div className="flex gap-2">
                <Button type="submit" variant="primary" disabled={!day} loading={save.isPending}>
                  Enregistrer
                </Button>
                <Button type="button" variant="ghost" onClick={() => setAnswer('ask')}>
                  Retour
                </Button>
              </div>
            </form>
          )}
          {answer === 'no' && (
            <>
              <p className="mt-1 text-sm text-body">Demandez à votre auto-école comment vous entraîner au code de la route.</p>
              <SchoolMessagesLink onOpen={onOpenMessages} />
              <div className="mt-3">
                <Button variant="soft" onClick={closeNo}>
                  OK
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function Icon() {
  return (
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary" aria-hidden>
      <Signpost size={18} />
    </span>
  );
}
